import {
	Grid,
	Box,
	CircularProgress,
	Checkbox,
	Typography,
} from '@mui/material';
import { Stack } from '@mui/system';

import React, { useCallback, useEffect, useState } from 'react';

import { useSelector, useDispatch } from 'react-redux';

import { useParams, useNavigate } from 'react-router-dom';

import AccordionComp from 'common/components/Accordion/AccordionComp';
import ButtonComp from 'common/components/Button/ButtonComp';
import SelectDropdown from 'common/components/Select/SelectDropdown';
import SelectableHOC from 'common/components/SelectableHOC/SelectableHOC';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import { MODULE_NAME, TABLE_CONSTANTS, TASKTYPE } from 'common/constants';
import {
	PROGRESS_BAR_ELEMENTS,
	APPLICABILITY_STATUS,
	APPLICABILITY_STATUS_LABELS,
	STATUS_UPDATE_TYPE,
} from 'common/constants/CitationDetails';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { rrcpDashboardRoutes } from 'common/constants/rrcp-dashboard-routes';
import {
	AXIOS_NETWORK_ERR_CODE,
	submissionFailedMsg,
} from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import { setToastMessage } from 'common/store/reducer/commonReducer';
import { setIsOpenInvententoryCommentDrawer } from 'common/store/reducer/regulationInventoryReducer';
import { toSentenceCase, urlWithTenant } from 'common/utils/utilFunctions';

import useTableQuery from 'hooks/useTableQuery';

import {
	getCitationDetails,
	updateCitationsDetailsPost,
	updateCitationsDetailsPut,
} from '../api';
import {
	ISubPartCitationDetails,
	ICitationDetailsElements,
	ICitationsUpdateRequest,
	ICitationsAssessment,
} from '../api/type-def';
import CitationDescription from '../Components/CitationDescription/CitationDescription';
import RegulationBreadcrumbs, {
	ICustomBreadcrumbsProps,
} from '../Components/RegulationBreadcrumb/RegulationBreadcrumbs';
import RegulationDetailsHeader from '../Components/RegulationHeader/RegulationDetailsHeader';

import NotApplicableCitation from './NotApplicableCitation/NotApplicableCitation';
import SubCitationSummary from './SubCitationSummary/SubCitationSummary';

import detailsStyles from './RegulationInventoryDetails.module.scss';

const { textBoxColor } = detailsStyles;

const { REQUIREMENT_INVENTORY_NAME } = MODULE_NAME;

const { WORKFLOW } = TASKTYPE;

const { SAVE, REVIEW, APPROVE, REJECT } = STATUS_UPDATE_TYPE;

const { GROUPING_ASSESSMENT, APPLICABILITY_ASSESSMENT, APPLICABILITY_REVIEW } =
	PROGRESS_BAR_ELEMENTS;

const { APPLICABLE, NOTAPPLICABLE } = APPLICABILITY_STATUS;

const { OK, BAD_REQUEST, NOT_FOUND } = HTTP_STATUS_CODES;

const { regulationInventory, overView } = rrcpDashboardRoutes;

interface IRegulationInventoryProps {
	storageWrapper: any;
}

const RegulationInventoryDetails = (props: IRegulationInventoryProps) => {
	const { storageWrapper } = props;
	const { subPartId } = useParams();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const isCommentAdded = useSelector(
		(state: RootState) => state.regulationInventory.isCommentAdded,
	);
	const commentDrawer = useSelector(
		(state: RootState) =>
			state.regulationInventory.isOpenInvententoryCommentDrawer,
	);
	const { isToastOpen, message, severity } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);

	const loggedInUser = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);

	const [citation, setCitation] = useState<ISubPartCitationDetails>();
	const [loading, setIsLoading] = useState<boolean>(false);
	const [checkedCitations, setCheckedCitations] = useState<
		ICitationDetailsElements[]
	>([]);
	const [selectedAccordians, setSelectedAccordians] = useState<
		ICitationDetailsElements[]
	>([]);
	const [shouldEnableSubmit, setShouldEnableSubmit] = useState<boolean>(false);
	const [selectedDropDownStatus, setSelectedDropDownStatus] =
		useState<string>(APPLICABLE);
	const [isRejected, setIsRejected] = useState<boolean>(false);
	const [isCommentsAdded, setIsCommentsAdded] = useState<boolean>(true);

	const handleChange = (comments: string, res: ICitationDetailsElements) => {
		res.applicabilityComments = comments;
		if (comments.length === 0) {
			setIsCommentsAdded(false);
		}
	};

	const isTextFieldDisabled = loggedInUser?.userId !== citation?.assignedUserId;

	const isAssignedUserSameAsLoggedInUser =
		loggedInUser?.userId === citation?.assignedUserId;

	const handleCheckBox = (
		event: React.SyntheticEvent,
		val: ICitationDetailsElements,
	) => {
		if ((event.target as HTMLInputElement).checked) {
			setCheckedCitations([...checkedCitations, val]);
		} else {
			setCheckedCitations((res) =>
				res.filter((accordian) => accordian !== val),
			);
		}
	};

	const handleSelectAllCheckBox = (event: React.SyntheticEvent) => {
		event.stopPropagation();
		if ((event.target as HTMLInputElement).checked) {
			setCheckedCitations(citation?.applicabilityAssessment.children ?? []);
		} else {
			setCheckedCitations([]);
		}
	};

	const handleAccordianChange = (
		val: ICitationDetailsElements,
		event: React.SyntheticEvent,
		comments?: string,
	) => {
		event.stopPropagation();
		val.applicabilityComments = comments || val.applicabilityComments;
		enableSubmitButton();
		if (selectedAccordians.includes(val)) {
			setSelectedAccordians((res) =>
				res.filter((accordian) => accordian !== val),
			);
		} else {
			setSelectedAccordians([...selectedAccordians, val]);
		}
	};

	const onApplyClick = () => {
		changeMultipleCitationStatus(checkedCitations, selectedDropDownStatus);
		setCheckedCitations([]);
	};

	const onHandleCancel = () => {
		setCheckedCitations([]);
	};

	const onchangeValue = (arg: string) => {
		setSelectedDropDownStatus(arg);
	};

	const submitForReviewHandler = () => {
		changeMultipleCitationStatus(
			citation?.applicabilityAssessment.children ?? [],
			'',
			APPLICABILITY_REVIEW,
			true,
			REVIEW,
		);
	};

	const onApplicabilityReject = () => {
		dispatch(setIsOpenInvententoryCommentDrawer(true));
		setIsRejected(true);
	};

	const handleToastHide = useCallback(() => {
		dispatch(
			setToastMessage({
				message: '',
				severity: '',
				isToastOpen: false,
			}),
		);
	}, []);

	const { updateTableQuery: updateApplicabilityRegTableQuery } = useTableQuery({
		id: 'ApplicabilityRegTable',
	});

	const onApplicabilityApproval = () => {
		changeMultipleCitationStatus(
			citation?.applicabilityAssessment.children ?? [],
			'',
			GROUPING_ASSESSMENT,
			true,
			APPROVE,
		);

		const defaultQuery = {
			pageNumber: 0,
			pageSize: TABLE_CONSTANTS.customPageSize,
		};
		updateApplicabilityRegTableQuery(defaultQuery);
	};

	const getPayloadForApplicabilityAssessment = (
		citations: ICitationDetailsElements[],
		status?: string,
	) =>
		citations.map((res) => ({
			applicabilityStatus: status || res.applicabilityStatus,
			applicabilityComments: res.applicabilityComments ?? '',
			regulationRelationshipId: res.id,
		}));

	const handleAccordionText = (rvalue: any, event: any) => {
		handleAccordianChange(rvalue, event);
	};

	const changeMultipleCitationStatus = async (
		multipleCitations: ICitationDetailsElements[],
		status?: string,
		taskStatus?: string,
		hasComeFromSubmitForReview?: boolean,
		actionType = SAVE,
	) => {
		const statusPresentCitations = multipleCitations.filter(
			(res) => res.applicabilityStatus,
		);
		const applicabilityAssessmentPut: ICitationsAssessment[] =
			getPayloadForApplicabilityAssessment(statusPresentCitations, status);
		const statusNotPresentCitations = multipleCitations.filter(
			(res) => !res.applicabilityStatus,
		);
		const applicabilityAssessmentPost: ICitationsAssessment[] =
			getPayloadForApplicabilityAssessment(statusNotPresentCitations, status);
		const submitReview = hasComeFromSubmitForReview ?? false;
		if (statusNotPresentCitations.length > 0 && citation) {
			const postObject = {
				taskDetails: {
					taskStatus: taskStatus || citation?.taskStatus,
					taskId: citation.taskId,
					sectionId: citation.applicabilityAssessment?.id,
					sectionName: citation.applicabilityAssessment?.title,
					assessmentUpdate: false,
					taskType: WORKFLOW,
					moduleName: REQUIREMENT_INVENTORY_NAME,
					actionType,
				},
				applicabilityAssessment: applicabilityAssessmentPost,
			};
			const respPost: any = await updateCitationsDetailsPost(postObject);
			parseCitationStatusUpdateResponse(respPost, submitReview);
		}

		if (statusPresentCitations.length > 0 && citation) {
			const putObject = {
				taskDetails: {
					taskStatus: taskStatus || citation.taskStatus,
					taskId: citation.taskId,
					sectionId: citation.applicabilityAssessment?.id,
					sectionName: citation.applicabilityAssessment?.title,
					assessmentUpdate: !submitReview,
					taskType: WORKFLOW,
					moduleName: REQUIREMENT_INVENTORY_NAME,
					actionType,
				},
				applicabilityAssessment: applicabilityAssessmentPut,
			};
			const respPut: any = await updateCitationsDetailsPut(putObject);
			parseCitationStatusUpdateResponse(respPut, submitReview);
		}
	};
	const fetchCitationsData = async () => {
		setIsLoading(true);
		const resp: any = await getCitationDetails(subPartId ?? '');
		setIsLoading(false);
		if (resp?.statusCode === OK) {
			setCitation(resp?.responseBody);
		}
		if (resp?.errorBody || resp?.error) {
			const { errorBody } = resp;
			const responseError: any = resp.error;
			const errorMessage =
				(errorBody?.errorMessage as string) ||
				(responseError?.message as string);
			dispatch(
				setToastMessage({
					message: errorMessage,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
	};

	useEffect(() => {
		if (isRejected && isCommentAdded && !commentDrawer) {
			fetchCitationsData();
		}
	}, [isRejected, isCommentAdded, commentDrawer]);

	useEffect(() => {
		fetchCitationsData();
	}, []);

	const enableSubmitButton = () => {
		const checkForNotApplicableComments =
			citation?.applicabilityAssessment?.children.every((res) => {
				if (res.applicabilityStatus === NOTAPPLICABLE) {
					return (
						res.applicabilityComments && res.applicabilityComments.length > 0
					);
				}
				return true;
			});

		const citationStatus = citation?.applicabilityAssessment?.children.every(
			(res) => res.applicabilityStatus,
		);

		if (citationStatus && checkForNotApplicableComments) {
			setShouldEnableSubmit(true);
		} else {
			setShouldEnableSubmit(false);
		}
	};
	const handleChangeCitaionStatus = async (event: any, val: any, res: any) => {
		setIsCommentsAdded(true);
		event.stopPropagation();
		if (citation) {
			const object = {
				taskDetails: {
					taskStatus: citation?.taskStatus,
					taskId: citation?.taskId,
					sectionId: citation?.applicabilityAssessment?.id,
					sectionName: citation?.applicabilityAssessment?.title,
					assessmentUpdate: !!res.applicabilityStatus,
					taskType: WORKFLOW,
					moduleName: REQUIREMENT_INVENTORY_NAME,
					actionType: SAVE,
				},
				applicabilityAssessment: [
					{
						applicabilityStatus: val ?? res.applicabilityStatus,
						applicabilityComments: res.applicabilityComments,
						regulationRelationshipId: res.id,
					},
				],
			};

			let resp: any;
			if (!res.applicabilityStatus) {
				resp = await updateCitationsDetailsPost(object);
			} else {
				resp = await updateCitationsDetailsPut(object);
			}
			parseCitationStatusUpdateResponse(resp, false);
		}
	};
	const changeCitationsStatus =
		(res: ICitationDetailsElements, val?: string) =>
		// eslint-disable-next-line indent
		async (event: React.SyntheticEvent) =>
			// eslint-disable-next-line indent
			handleChangeCitaionStatus(event, val, res);

	const handleSucess = (resp: any, hasComeFromSubmitForReview: boolean) => {
		const response: ICitationsUpdateRequest = resp?.responseBody;
		const citations = citation?.applicabilityAssessment.children.map((res) => {
			response.applicabilityAssessment.forEach((val) => {
				if (res.id === val.regulationRelationshipId) {
					res.applicabilityStatus = val.applicabilityStatus;
					res.applicabilityComments = val.applicabilityComments;
				}
			});
			return res;
		});
		setCitation({
			...(citation as ISubPartCitationDetails),
			applicabilityAssessment: {
				...(citation?.applicabilityAssessment as ICitationDetailsElements),
				children: citations ?? [],
			},
		});
		if (hasComeFromSubmitForReview) {
			navigate(urlWithTenant(`/${regulationInventory}/${overView}`));
		} else {
			dispatch(
				setToastMessage({
					message: 'Details saved successfully',
					severity: 'success',
					isToastOpen: true,
				}),
			);
		}
	};

	const parseCitationStatusUpdateResponse = (
		resp: any,
		hasComeFromSubmitForReview: boolean,
	) => {
		if (resp?.responseBody) {
			handleSucess(resp, hasComeFromSubmitForReview);
		} else if (
			resp?.statusCode === BAD_REQUEST ||
			resp.statusCode === NOT_FOUND
		) {
			if (resp?.errorBody || resp?.error) {
				const { errorBody } = resp;
				const responseError: any = resp.error;
				const errorMsg =
					(errorBody?.errorMessage as string) ||
					(responseError?.message as string);
				dispatch(
					setToastMessage({
						message: errorMsg,
						severity: 'error',
						isToastOpen: true,
					}),
				);
			}
		} else if (
			resp?.error?.code === AXIOS_NETWORK_ERR_CODE &&
			hasComeFromSubmitForReview
		) {
			dispatch(
				setToastMessage({
					message: submissionFailedMsg,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
	};

	useEffect(() => {
		enableSubmitButton();

		if (citation && isRejected && isCommentAdded && !commentDrawer) {
			changeMultipleCitationStatus(
				citation?.applicabilityAssessment.children ?? [],
				'',
				APPLICABILITY_ASSESSMENT,
				true,
				REJECT,
			);
			setIsRejected(false);
		}
	}, [citation, isRejected, isCommentAdded, commentDrawer, isCommentsAdded]);

	const label = {
		inputProps: { 'aria-label': 'Checkbox demo', 'data-testid': 'checkbox' },
	};

	const breadCrumbText = useSelector(
		(state: RootState) => state.common.requirementInventoryBreadcrumbText,
	);

	const breadCrumbs: ICustomBreadcrumbsProps[] = [
		{
			title: toSentenceCase(breadCrumbText),
			type: 'link',
			key: 'req-inv',
			path: regulationInventory,
			dataTestId: 'inventory-breadcrumbs-link',
		},
		{
			title: '',
			type: 'link',
			key: 'req-inv-selected-tab',
			path: `${regulationInventory}/${overView}`,
			dataTestId: 'inventory-breadcrumbs-link-selected-tab',
		},
		{
			title: citation?.applicabilityAssessment?.title || '',
			type: 'text',
			key: 'static-text',
		},
	];

	return (
		<Grid container spacing={2}>
			{loading ? (
				<CircularProgress className={detailsStyles['progress-bar']} />
			) : (
				<>
					<Grid item xs={12} sm={12} md={12} lg={12} xl={12} pt={0}>
						<RegulationBreadcrumbs
							items={breadCrumbs}
							storageWrapper={storageWrapper}
						/>
					</Grid>
					<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
						{citation?.applicabilityAssessment && (
							<RegulationDetailsHeader
								taskId={citation.taskId}
								status={citation.taskStatus}
								assignedUserName={citation.assignedUserName}
								jurisdiction={citation.jurisdiction}
								reviewByDate={citation.reviewByDate}
								title={citation.applicabilityAssessment.title}
							/>
						)}
					</Grid>
					<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
						<SelectableHOC
							count={checkedCitations.length}
							element={
								<Box className={detailsStyles['dropdown-box']}>
									<Box className={detailsStyles['flex-action-elements']}>
										<SelectDropdown
											label="Applicability"
											name="Applicability"
											id="applicability-status"
											options={APPLICABILITY_STATUS_LABELS}
											mode="standard"
											inputProps={{
												'data-testid': 'applicability-status',
											}}
											onChange={onchangeValue}
											value={selectedDropDownStatus}
											className={detailsStyles['citation-dropdown']}
										/>
									</Box>
									<Box className={detailsStyles['flex-action-button-elements']}>
										<ButtonComp
											btnType="primary"
											compClass={detailsStyles['apply-button']}
											clickHandler={onApplyClick}
											data-testid="bulk-apply-btn"
										>
											Apply
										</ButtonComp>
									</Box>
								</Box>
							}
							handleCancelChange={onHandleCancel}
						>
							<Stack
								className={detailsStyles['citaion-deatils-wrapper']}
								gap="4px"
							>
								{(citation?.applicabilityAssessment?.children?.length || 0) >
									1 &&
									isAssignedUserSameAsLoggedInUser && (
										<Grid
											item
											xs={12}
											sm={12}
											md={12}
											lg={12}
											xl={12}
											display="flex"
										>
											<Checkbox
												data-testid="select-all-checkbox"
												{...label}
												size="small"
												checked={
													checkedCitations.length ===
													citation?.applicabilityAssessment.children?.length
												}
												onChange={(event) => handleSelectAllCheckBox(event)}
												className={detailsStyles['select-all-checkbox']}
												sx={{
													'&.Mui-checked': {
														color: textBoxColor,
													},
												}}
											/>
											<Typography className={detailsStyles['select-all-text']}>
												Select All
											</Typography>
										</Grid>
									)}
								{citation?.applicabilityAssessment.children.map((res) => (
									<AccordionComp
										className={
											res.applicabilityStatus === NOTAPPLICABLE &&
											!res.applicabilityComments
												? detailsStyles['accordian-margin-coloured']
												: detailsStyles['accordion-margin-btm']
										}
										expanded={selectedAccordians.includes(res)}
										onChange={(event) => handleAccordianChange(res, event)}
										isLeftAccordian
										key={res.id}
										dataTestId="expandButton"
										summary={
											<SubCitationSummary
												checkedCitations={checkedCitations}
												selectedAccordians={selectedAccordians}
												res={res}
												isCheckBoxRequired
												handleCheckBox={handleCheckBox}
												handleClick={changeCitationsStatus}
												enableApplicablityStatus={
													isAssignedUserSameAsLoggedInUser
												}
												isTaskidPresent={citation.taskId !== null}
												handleAccordionText={handleAccordionText}
											/>
										}
										details={
											<div
												className={detailsStyles['inventory-accordian-detail']}
											>
												<CitationDescription
													currentNode={res}
													parentNode={null}
													isMultiCheckBoxRequired={false}
												/>
												{res.applicabilityStatus === NOTAPPLICABLE && (
													<NotApplicableCitation
														item={res}
														handleChange={(arg: string) =>
															handleChange(arg, res)
														}
														handleClick={changeCitationsStatus(res)}
														handleCancelClick={(
															e: React.SyntheticEvent,
															comments: string,
														) => handleAccordianChange(res, e, comments)}
														isTextBoxDisabled={isTextFieldDisabled}
													/>
												)}
											</div>
										}
									/>
								))}
							</Stack>
						</SelectableHOC>

						{isAssignedUserSameAsLoggedInUser &&
							shouldEnableSubmit &&
							citation?.taskStatus === APPLICABILITY_ASSESSMENT && (
								<Grid
									container
									className={detailsStyles['inventory-submit-for-review-grid']}
									justifyContent="end"
								>
									<ButtonComp
										btnType="primary"
										clickHandler={submitForReviewHandler}
										data-testid="submit-for-review"
									>
										Submit for Review
									</ButtonComp>
								</Grid>
							)}
						{isAssignedUserSameAsLoggedInUser &&
							citation?.taskStatus === APPLICABILITY_REVIEW && (
								<Grid
									container
									spacing={2}
									justifyContent="flex-end"
									className={detailsStyles['inventory-submit-for-review-grid']}
								>
									<Grid item xs={12} sm={4} md={2} lg={2} xl={1}>
										<ButtonComp
											btnType="secondary"
											clickHandler={onApplicabilityReject}
										>
											Reject
										</ButtonComp>
									</Grid>
									<Grid item xs={12} sm={4} md={2} lg={2} xl={1}>
										<ButtonComp
											btnType="primary"
											disabled={!shouldEnableSubmit}
											clickHandler={onApplicabilityApproval}
										>
											Approve
										</ButtonComp>
									</Grid>
								</Grid>
							)}
					</Grid>
				</>
			)}
			<AlertComponent
				open={isToastOpen ?? false}
				toastMessage={message ?? ''}
				severity={severity ?? ''}
				handleCloseMessage={handleToastHide}
				vertical="top"
				horizontal="right"
			/>
		</Grid>
	);
};

export default RegulationInventoryDetails;
